import { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { X, FileJson, FileSpreadsheet, Download, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_DEFS = [
  { id: 'basic',        label: 'Basic Info',         desc: 'Company, score, grade, date, summary' },
  { id: 'scores',       label: 'V3 Scores',          desc: 'Intent, Budget, Authority, Timeline, ICP, Risk (0-5)' },
  { id: 'reasoning',    label: 'Scoring Reasoning',  desc: 'AI explanation for each score' },
  { id: 'signals',      label: 'Signals',            desc: 'Detected signals & key insights' },
  { id: 'objections',   label: 'Objections',         desc: 'Buyer objections with severity' },
  { id: 'coaching',     label: 'Coaching Metrics',   desc: 'Discovery, question depth, talk ratio' },
  { id: 'next_actions', label: 'Next Actions',       desc: 'Recommended actions, next steps, competitors' }
];

/**
 * Modal for exporting calls in CSV / JSON.
 * Self-contained: handles its own format/fields state and the download action.
 */
export default function ExportModal({ onClose, period = 'monthly' }) {
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportFields, setExportFields] = useState(['basic', 'scores']);
  const [exporting, setExporting] = useState(false);

  const toggleField = (field) => {
    setExportFields(prev => prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]);
  };

  const handleExport = async () => {
    if (exportFields.length === 0) {
      toast.error('Select at least one field group');
      return;
    }
    setExporting(true);
    try {
      const res = await fetch(`${API_URL}/api/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        // Include the selected dashboard period so the export honours the
        // same time window shown on-screen (weekly / monthly / all).
        body: JSON.stringify({ format: exportFormat, fields: exportFields, call_ids: [], period })
      });
      if (!res.ok) throw new Error('Export failed');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_export_${new Date().toISOString().slice(0, 10)}.${exportFormat === 'csv' ? 'csv' : 'json'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${exportFormat.toUpperCase()}`);
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <Card className="bg-card border-border w-full max-w-md mx-4">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-lg">Export Data</CardTitle>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Format */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Format</Label>
            <div className="flex gap-2">
              <button
                onClick={() => setExportFormat('csv')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-colors ${
                  exportFormat === 'csv' ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background text-muted-foreground hover:bg-secondary/50'
                }`}
                data-testid="export-csv-btn"
              >
                <FileSpreadsheet className="w-4 h-4" />
                CSV
              </button>
              <button
                onClick={() => setExportFormat('json')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-colors ${
                  exportFormat === 'json' ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background text-muted-foreground hover:bg-secondary/50'
                }`}
                data-testid="export-json-btn"
              >
                <FileJson className="w-4 h-4" />
                JSON
              </button>
            </div>
          </div>

          {/* Fields */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Include Fields</Label>
            {FIELD_DEFS.map(({ id, label, desc }) => (
              <label
                key={id}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  exportFields.includes(id) ? 'border-primary/50 bg-primary/5' : 'border-border bg-background hover:bg-secondary/30'
                }`}
                data-testid={`export-field-${id}`}
              >
                <Checkbox
                  checked={exportFields.includes(id)}
                  onCheckedChange={() => toggleField(id)}
                  className="mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium text-foreground">{label}</p>
                  <p className="text-xs text-muted-foreground">{desc}</p>
                </div>
              </label>
            ))}
          </div>

          <Button
            onClick={handleExport}
            disabled={exporting || exportFields.length === 0}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="export-download-btn"
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Download {exportFormat.toUpperCase()}
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
